import { Users, Award, Briefcase, Building2, FileText, Mail, Phone, ArrowRight } from "lucide-react"
import { Button } from "../../components/ui/button"
import teamRecteur from "../../assets/team/recteur.jpg"
import teamViceRecteur from "../../assets/team/v-recteur.jpg"
import teamDAF from "../../assets/team/DAF.jpg"
import teamBenign from "../../assets/team/benign.jpg"
import DrBeatrice from "../../assets/team/DrBeatrice.jpg"

export default function Direction() {
    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            {/* Hero Section - Standard (ref: DroitEcoGestion) */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Administration</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Vol. 2024</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Leadership &</span>
                            <span className="block text-white mt-2">Gouvernance</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Une équipe dévouée à l'excellence académique et au développement stratégique de l'institution.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Contacter la Direction
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats Bar */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "Rectorat", label: "Direction" },
                        { val: "Académique", label: "Services" },
                        { val: "Recherche", label: "Innovation" },
                        { val: "Qualité", label: "Assurance" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {["Equipe", "Responsabilites", "Mission"].map((item, i) => (
                                <a key={i} href={`#${item.toLowerCase()}`} className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item}
                                </a>
                            ))}
                        </nav>
                        <div className="mt-12 pt-8 border-t border-border">
                            <div className="bg-primary/5 p-6 border-l-4 border-primary">
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-4 text-primary">En Bref</h4>
                                <p className="text-[11px] text-muted-foreground leading-relaxed">
                                    Le Rectorat est l'organe exécutif suprême de l'Université, assurant la mise en œuvre des décisions du Conseil d'Administration.
                                </p>
                            </div>
                        </div>
                    </aside>

                    {/* Content */}
                    <main className="space-y-16">
                        {/* Section 1: Team */}
                        <section id="equipe">
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Équipe de Direction
                            </h2>
                            <div className="space-y-24">
                                {[
                                    { 
                                        role: "Recteur", 
                                        name: "Dr Abbé Apollinaire BANGAYIMBAGA, PhD", 
                                        email: "bangaapo@gmail.com", 
                                        icon: Award, 
                                        image: teamRecteur,
                                        bio: "Nommé Recteur de l’Université de Ngozi en décembre 2007 après une année comme Secrétaire général de l’institution, il assure la direction de l’Université en collaboration avec les autres organes. Il est détenteur d’un doctorat en théologie morale obtenu à l’Université pontificale de Madrid." 
                                    },
                                    { 
                                        role: "Directeur des Services Académiques", 
                                        name: "Dr Abbé Lambert RIYAZIMANA, PhD", 
                                        email: "academia@univ-ngozi.bi", 
                                        icon: Briefcase, 
                                        image: teamViceRecteur,
                                        bio: "Pilote la Direction des Services Académiques depuis octobre 2023. Il anime et coordonne la mise en œuvre des Politiques et Stratégies d’enseignement. Il est détenteur d’un Doctorat en Communication Institutionnelle obtenu à Rome." 
                                    },
                                    { 
                                        role: "Directeur de Recherche et Innovation", 
                                        name: "Dr Soeur Béatrice NTAKAMURENGA, PhD", 
                                        email: "recherche@univ-ngozi.bi", 
                                        icon: Award, 
                                        image: DrBeatrice,
                                        bio: "Supervise les activités de recherche et promeut l'innovation au sein de l'institution. Elle coordonne les projets scientifiques et les publications de l'université." 
                                    },
                                    { 
                                        role: "Directeur d'Assurance Qualité", 
                                        name: "Dr Soeur Bénigne NGENDAKUMANA, PhD", 
                                        email: "qualite@univ-ngozi.bi", 
                                        icon: Briefcase, 
                                        image: teamBenign,
                                        bio: "Veille au maintien des standards académiques et à l'amélioration continue des processus d'enseignement et d'apprentissage." 
                                    },
                                    { 
                                        role: "Directeur Administratif, Financier et du Patrimoine", 
                                        name: "Msc NDAYISENGA Libère", 
                                        email: "administration@univ-ngozi.bi", 
                                        icon: Building2, 
                                        image: teamDAF,
                                        bio: "Assure la gestion rigoureuse des ressources humaines, financières et matérielles de l'université pour soutenir sa mission académique." 
                                    }
                                ].map((member, idx) => (
                                    <div key={idx} className="flex flex-col md:flex-row gap-12 items-start group">
                                        <div className="w-full md:w-[320px] shrink-0 aspect-[4/5] overflow-hidden border-2 border-border/50 transition-all hover:border-accent relative">
                                            <img
                                                src={member.image}
                                                alt={member.name}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                            <div className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center border border-accent/20">
                                                <member.icon className="w-5 h-5 text-primary" />
                                            </div>
                                        </div>
                                        <div className="flex-1 space-y-6">
                                            <div>
                                                <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-accent mb-2">{member.role}</h3>
                                                <p className="text-2xl lg:text-3xl font-serif font-black italic text-foreground leading-tight">{member.name}</p>
                                            </div>
                                            <div className="relative">
                                                <div className="absolute -left-6 top-0 bottom-0 w-1 bg-accent/20 rounded-full" />
                                                <p className="text-base text-muted-foreground leading-relaxed font-medium">
                                                    {member.bio}
                                                </p>
                                            </div>
                                            <div className="pt-6 border-t border-border/50 flex flex-wrap gap-6 items-center">
                                                {/* <div className="flex items-center gap-3 text-xs text-primary font-black uppercase tracking-widest">
                                                    <Mail className="w-4 h-4" />
                                                    {member.email}
                                                </div> */}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Section 2: Responsibilities */}
                        <section id="responsabilites">
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Domaines de Responsabilité
                            </h2>
                            <div className="grid md:grid-cols-2 gap-x-8 gap-y-10">
                                {[
                                    { t: "Gouvernance Stratégique", d: "Définition et suivi du plan stratégique de développement." },
                                    { t: "Excellence Académique", d: "Supervision de la qualité de l'enseignement et des programmes." },
                                    { t: "Gestion Administrative", d: "Optimisation des ressources humaines et matérielles." },
                                    { t: "Rayonnement", d: "Développement des partenariats nationaux et internationaux." }
                                ].map((item, i) => (
                                    <div key={i} className="relative pl-6 border-l-2 border-border group hover:border-accent transition-colors">
                                        <h3 className="text-sm font-bold uppercase mb-2 text-foreground group-hover:text-primary transition-colors">{item.t}</h3>
                                        <p className="text-xs text-muted-foreground leading-relaxed">{item.d}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Mission Quote */}
                        <section id="mission" className="bg-secondary/20 p-8 rounded-md border-l-4 border-accent">
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-accent mb-4">Notre Mission</h3>
                            <p className="text-base font-serif italic leading-relaxed text-foreground/80">
                                "La direction s'engage à créer un environnement d'apprentissage inclusif et de qualité, renforçant la position de l'université comme institution de référence."
                            </p>
                        </section>

                    </main>
                </div>
            </article>
        </div>
    )
}
